// A sticky-header table of rounds x players with a sticky totals row. Purely
// a display component: it knows nothing about scoring rules or what a round
// means - the caller supplies `cellContent` (how a cell renders) and
// `onCellClick` (what happens when one is clicked), so this same grid can
// back Halve It's independently-clickable cells today, and later Cricket/
// Tactics' turn-fed marks display, without this file changing.

// cellContent(player, roundIndex) must return { html, classes }, where
// `classes` is a list of extra classes on top of the base "grid-cell" (e.g.
// ['filled'], ['filled', 'halved'], or [] for an empty cell).

export function mountRoundsGrid(container, { rounds, players, cellContent, onCellClick, cornerContent = 'Round', onCornerClick }) {
    const columnTemplate = '100px ' + `repeat(${players.length}, minmax(64px, 1fr))`;

    let gridHTML = '<div class="score-grid-container">';
    gridHTML += `<div class="score-grid" style="grid-template-columns: ${columnTemplate};">`;

    // Header row - the corner cell defaults to a plain "Round" label (Halve
    // It), but a game can hand it interactive HTML instead (Cricket's
    // add/remove mode toggle) plus a click handler for it.
    gridHTML += `<div class="grid-header header-row round-column-header" id="gridCornerHeader">${cornerContent}</div>`;
    players.forEach(player => {
        gridHTML += `<div class="grid-header header-row">${player}</div>`;
    });

    // Score rows
    rounds.forEach((round, roundIndex) => {
        // Rows are 1-indexed, +1 again for the header row above them. Every
        // item in this row is placed on an explicit grid-row/grid-column
        // (not left to auto-placement) - a grid-row-strike below spans every
        // column on the same row, and CSS Grid resolves ALL fully-explicit
        // items in one pass BEFORE any auto-placed ones regardless of DOM
        // order, so if these cells were left auto they'd get shoved past
        // every strike row (which fully occupies its row) into entirely
        // different rows instead of sharing this one.
        const gridRow = roundIndex + 2;
        gridHTML += `<div class="grid-header" style="grid-row: ${gridRow}; grid-column: 1;">${round.name}<span class="round-label">${round.description}</span></div>`;
        players.forEach((player, playerIndex) => {
            const { html, classes } = cellContent(player, roundIndex);
            const classAttr = ['grid-cell', ...classes].join(' ');
            gridHTML += `<div class="${classAttr}" id="cell-${player}-${roundIndex}" style="grid-row: ${gridRow}; grid-column: ${playerIndex + 2};">${html}</div>`;
        });
        // A plain grid item spanning every column on this same row, toggled
        // visible by updateRowStrike() once a caller (e.g. a closing game,
        // once a target is dead for every player) says so. Overlapping grid
        // items are entirely normal in CSS Grid; pointer-events:none keeps
        // it from blocking clicks on the real cells underneath.
        gridHTML += `<div class="grid-row-strike" id="strike-${roundIndex}" style="grid-row: ${gridRow};"></div>`;
    });

    gridHTML += '</div>'; // Close score-grid

    // Sticky total row - using same column template
    gridHTML += `<div class="totals-row" style="grid-template-columns: ${columnTemplate};">`;
    gridHTML += '<div class="grid-header grid-header-total">TOTAL</div>';
    players.forEach(player => {
        gridHTML += `<div class="total-cell" id="total-${player}">0</div>`;
    });
    gridHTML += '</div>'; // Close totals-row

    gridHTML += '</div>'; // Close score-grid-container
    container.innerHTML = gridHTML;

    // Wired programmatically (rather than onclick="..." strings) since this
    // component owns real DOM references and has no reason to leak into
    // global scope.
    if (onCellClick) {
        players.forEach(player => {
            rounds.forEach((_, roundIndex) => {
                document.getElementById(`cell-${player}-${roundIndex}`)
                    .addEventListener('click', () => onCellClick(player, roundIndex));
            });
        });
    }
    if (onCornerClick) {
        document.getElementById('gridCornerHeader').addEventListener('click', onCornerClick);
    }
}

export function updateCell(player, roundIndex, { html, classes }) {
    const cell = document.getElementById(`cell-${player}-${roundIndex}`);
    if (!cell) return;
    // Reset to the base class rather than removing a fixed set of names, since
    // different games use different class vocabularies for a cell's state.
    cell.className = ['grid-cell', ...classes].join(' ');
    cell.innerHTML = html;
}

export function updateTotal(player, total) {
    const cell = document.getElementById(`total-${player}`);
    if (cell) cell.textContent = total;
}

export function updateCornerContent(html) {
    const el = document.getElementById('gridCornerHeader');
    if (el) el.innerHTML = html;
}

export function updateRowStrike(roundIndex, dead) {
    const el = document.getElementById(`strike-${roundIndex}`);
    if (el) el.classList.toggle('dead', dead);
}
